import { useRef, type ReactNode } from "react";

import {
  createBroadcastComposeStore,
  BroadcastComposeStoreContext,
  type BroadcastComposeState,
  type BroadcastComposeStoreApi,
} from "./broadcastComposeStoreFactory";
import { getOrCreateComposeStore } from "./broadcastComposeStoreRegistry";

export type {
  BroadcastComposeState,
  BroadcastComposeActions,
  BroadcastComposeStore,
  BroadcastComposeStoreApi,
} from "./broadcastComposeStoreFactory";

export {
  createBroadcastComposeStore,
  useBroadcastComposeStore,
  useBroadcastComposeStoreShallow,
  useBroadcastComposeActions,
  useBroadcastComposeStoreApi,
  useIsAbDurationInvalid,
} from "./broadcastComposeStoreFactory";

export type {
  VariationDraft,
  EmailVariationContent,
  AbTestConfig,
  WinningMetric,
  TestDurationUnit,
} from "./variationTypes";

export {
  MAX_VARIATIONS,
  makeEmailVariation,
  makeSmsVariation,
  isAbEnabled,
} from "./variationTypes";

// --- Provider ---

export interface BroadcastComposeProviderProps {
  initialState?: Partial<BroadcastComposeState>;
  /**
   * Opt-in draft persistence: when set, the store is fetched from (or created
   * in) the module registry under this key and survives unmounts until
   * `clearComposeStore(key)` is called. `initialState` only applies when the
   * registry entry is created. When absent, a fresh store is created per
   * mount as before.
   */
  draftKey?: string;
  children: ReactNode;
}

export function BroadcastComposeProvider({
  initialState,
  draftKey,
  children,
}: BroadcastComposeProviderProps) {
  const storeRef = useRef<BroadcastComposeStoreApi>(undefined);
  // Route components re-render (not remount) on param change, so a draftKey
  // change (e.g. site switch) must swap stores rather than keep the stale ref.
  const keyRef = useRef<string | undefined>(undefined);
  if (!storeRef.current || keyRef.current !== draftKey) {
    keyRef.current = draftKey;
    storeRef.current = draftKey
      ? getOrCreateComposeStore(draftKey, initialState)
      : createBroadcastComposeStore(initialState);
  }
  return (
    <BroadcastComposeStoreContext.Provider value={storeRef.current}>
      {children}
    </BroadcastComposeStoreContext.Provider>
  );
}
